/**
 * VoiceReportDoc — the voice assessment, read in the main window.
 *
 * The side column carries the numbers; the prose lives here. Two reasons.
 * A 344px strip turns three sentences of dense criticism into a wall — the
 * first version shipped exactly that, clamped to four lines, which is a
 * confession that it did not belong there. And the assessment is the half of
 * this feature that reproduces: the score moves several points between runs
 * of the same piece, while the diagnosis names the same faults every time. The
 * part worth reading deserves the width to be read in.
 *
 * Composed from the piece's own columns rather than a `piece_reports` row, the
 * way the Piece Summary is. That is what lets it work for the 161 pieces
 * scored by backfill, which have scores and notes but never produced a report.
 */

import type { PieceDetail } from "@/types/piece";
import { useRole } from "@/lib/useRole";
import { unscoredVoiceState } from "@/lib/voice-score-action";
import { ScoreVoiceButton } from "@/components/ScoreVoiceButton";
import {
  voiceDimensions,
  voiceAggregate,
  voiceGateState,
  VOICE_AGGREGATE_GATE,
  VOICE_AGGREGATE_MAX,
  VOICE_CATEGORY_FLOOR,
  VOICE_MAX,
} from "@shared/voice-scores";

const QUALITY_GATE = 70;

/** The evaluator writes several paragraphs; a single <p> would run them together. */
function paragraphs(text: string): string[] {
  return text
    .split(/\n{2,}|\n/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export function VoiceReportDoc({
  piece,
  edited,
}: {
  piece: PieceDetail;
  /** Whether the piece has a corrected draft — the Editor has run on it. */
  edited: boolean;
}) {
  const { canEdit } = useRole();
  const dims = voiceDimensions(piece);
  const total = voiceAggregate(piece);
  const state = voiceGateState(piece);
  const quality = piece.quality_score;
  const unscored = unscoredVoiceState({
    hasScoresOrNotes: total != null || !!piece.voice_evaluator_notes,
    edited,
  });

  return (
    <div className="voice-report">
      <div className="voice-report-scores">
        {quality != null && (
          <section className="voice-report-score">
            <h2>Tone &amp; style</h2>
            <p className="voice-report-figure">
              <strong>{quality}</strong>
              <span>/100</span>
              <em>gate {QUALITY_GATE}</em>
            </p>
            <p className="voice-report-source">
              The Auditor&rsquo;s overall quality score, set before the Editor ran.
              {piece.tone_style_score != null && (
                <> Its tone &amp; style line scored {piece.tone_style_score}/{VOICE_MAX}.</>
              )}
            </p>
          </section>
        )}

        {total != null && (
          <section className="voice-report-score">
            <h2>Voice fidelity</h2>
            <p className="voice-report-figure">
              <strong>{total}</strong>
              <span>/{VOICE_AGGREGATE_MAX}</span>
              <em>gate {VOICE_AGGREGATE_GATE}</em>
            </p>
            <table className="voice-report-dims">
              <tbody>
                {dims.map((d) => (
                  <tr key={d.key} className={d.under ? "is-under" : undefined}>
                    <th scope="row">{d.label}</th>
                    <td>
                      {d.score == null ? "—" : `${d.score}/${VOICE_MAX}`}
                      {d.under && <span className="voice-report-flag">under {VOICE_CATEGORY_FLOOR}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="voice-report-source">
              Scored against the publication&rsquo;s voice profile after the Editor
              finished — the prose that actually ships, not the draft.
            </p>
          </section>
        )}
      </div>

      {/* Stated on the report as well as in the column, because a reader who
          arrives here from a link has not necessarily seen the column. */}
      {state === "under_floor" && (
        <p className="voice-report-verdict">
          One dimension is under {VOICE_CATEGORY_FLOOR}. The total can look
          acceptable while a single dimension is broken, which is why the floor
          exists alongside it.
        </p>
      )}
      {state === "under_aggregate" && (
        <p className="voice-report-verdict">
          Under {VOICE_AGGREGATE_GATE} overall.
        </p>
      )}

      {piece.voice_evaluator_notes && (
        <section className="voice-report-notes">
          <h2>The assessment</h2>
          {paragraphs(piece.voice_evaluator_notes).map((t, i) => (
            <p key={i}>{t}</p>
          ))}
        </section>
      )}

      {piece.voice_notes && (
        <section className="voice-report-notes voice-report-notes-prior">
          <h2>Before editing</h2>
          {paragraphs(piece.voice_notes).map((t, i) => (
            <p key={i}>{t}</p>
          ))}
          <p className="voice-report-source">
            The Auditor&rsquo;s reading of the draft, written before the Editor
            changed it. Kept apart from the assessment above so it stays clear
            which prose each one judged.
          </p>
        </section>
      )}

      {unscored === "not_edited" && (
        <p className="voice-report-source">
          No voice fidelity scores yet. The piece is scored once the Editor has
          run on it.
        </p>
      )}
      {unscored === "unscored" && (
        <>
          <p className="voice-report-source">
            This piece has no voice fidelity scores. Scoring reads the edited
            draft against the publication&rsquo;s voice profile, with one call
            on your AI key.
          </p>
          {canEdit && <ScoreVoiceButton pieceId={piece.id} />}
        </>
      )}
    </div>
  );
}
